import { createFileRoute } from "@tanstack/react-router";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { cn } from "@/lib/utils/ui";
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Plus, Rocket, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import AssignClientHabitDialog from "@/components/habits/assignClientHabitDialog";
import { HabitDetailView } from "@/components/habits/HabitDetailView";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { trpc } from "@/lib/trpc/client";
import { ClientSidebarDetailLoadingState } from "@/components/client/ClientTabLoadingStates";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";

export const Route = createFileRoute(
  "/_auth/_dashboard/clients/$clientId/_layout/habits",
)({
  pendingComponent: ClientSidebarDetailLoadingState,
  component: HabitsPage,
});

type ClientHabit = {
  id: string;
  habit_id: string | null;
  name: string | null;
  unit: string | null;
  description: string | null;
  emoji: string | null;
  goal: number;
  frequency: string | null;
  archived: boolean | null;
  assigned_at: string;
};

const getCadenceLabel = (frequency: string | null | undefined) =>
  frequency === "weekly" ? "week" : "day";

function HabitsPage() {
  const { clientId } = Route.useParams();

  const { data, isLoading } = trpc.habits.listForClient.useQuery(
    { clientId },
    { enabled: Boolean(clientId) },
  );

  const validHabits = (data as ClientHabit[] | undefined) ?? [];

  const habits = useMemo(() => {
    if (!validHabits) return [];
    return validHabits.filter((h) => !h.archived);
  }, [validHabits]);

  const archivedHabits = useMemo(() => {
    if (!validHabits) return [];
    return validHabits.filter((h) => h.archived);
  }, [validHabits]);

  const [selectedHabit, setSelectedHabit] = useState<ClientHabit>();
  const [editHabit, setEditHabit] = useState<ClientHabit>();
  const [archivedExpanded, setArchivedExpanded] = useState(false);
  const [assignClientHabitDialogOpen, setAssignClientHabitDialogOpen] =
    useState(false);
  const [search, setSearch] = useState("");

  const filteredHabits = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return habits;
    }
    return habits.filter((habit) =>
      (habit.name ?? "Habit").toLowerCase().includes(query),
    );
  }, [habits, search]);

  const filteredArchivedHabits = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return archivedHabits;
    }
    return archivedHabits.filter((habit) =>
      (habit.name ?? "Habit").toLowerCase().includes(query),
    );
  }, [archivedHabits, search]);

  useEffect(() => {
    if (!selectedHabit && habits && habits.length > 0) {
      setSelectedHabit(habits[0]);
    }
  }, [habits]);

  useEffect(() => {
    if (!selectedHabit) return;
    if (selectedHabit.archived) {
      setArchivedExpanded(true);
    }
  }, [selectedHabit]);

  if (isLoading) {
    return <ClientSidebarDetailLoadingState />;
  }

  const renderHabit = (habit: ClientHabit) => {
    const habitName = habit.name ?? "Habit";
    const habitUnit = habit.unit ?? "";
    const cadenceLabel = getCadenceLabel(habit.frequency);
    const selected = selectedHabit?.id === habit.id;
    return (
      <button
        key={habit.id}
        type="button"
        className={cn(
          "w-full rounded-lg border p-3 text-left transition-colors hover:bg-muted/50",
          selected ? "border-primary bg-primary/5" : "border-border bg-card",
          habit.archived && "opacity-70",
        )}
        onClick={() => setSelectedHabit(habit)}
      >
        <h3 className="truncate font-medium leading-snug">
          {habit.emoji ? `${habit.emoji} ` : ""}
          {habitName}
        </h3>
        <p className="mt-2 truncate text-sm text-muted-foreground">
          {habit.goal} {habitUnit}/{cadenceLabel}
        </p>
      </button>
    );
  };

  return (
    <>
      <AssignClientHabitDialog
        clientId={clientId}
        open={assignClientHabitDialogOpen}
        onOpenChange={() => {
          setAssignClientHabitDialogOpen(false);
          setEditHabit(undefined);
        }}
        onHabitAdded={(habit) => {
          setSelectedHabit(habit);
        }}
        habit={editHabit}
      />
      <div className="mb-6 w-full min-w-0 overflow-x-hidden md:w-[90%] mx-auto px-4 lg:px-0">
        {habits?.length === 0 && (
          <Empty className="py-16">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Rocket className="size-6" />
              </EmptyMedia>
              <EmptyTitle>No habits yet</EmptyTitle>
              <EmptyDescription>
                Track client habits to help them reach their goals.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button
                variant="outline"
                onClick={() => setAssignClientHabitDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" /> Add First Habit
              </Button>
            </EmptyContent>
          </Empty>
        )}
        {(habits?.length ?? 0) > 0 && (
          <>
            {/* Mobile Dropdown */}
            <div className="lg:hidden mb-4">
              <div className="flex items-center gap-2">
                <Select
                  value={selectedHabit?.id || ""}
                  onValueChange={(value) => {
                    const habit = [...habits, ...archivedHabits].find(
                      (h) => h.id === value,
                    );
                    if (habit) setSelectedHabit(habit);
                  }}
                >
                  <SelectTrigger className="h-10 w-full">
                    <SelectValue placeholder="Select a habit" />
                  </SelectTrigger>
                  <SelectContent>
                    {habits?.map((habit) => (
                      <SelectItem key={habit.id} value={habit.id}>
                        {habit.emoji ? `${habit.emoji} ` : ""}
                        {habit.name ?? "Habit"}
                      </SelectItem>
                    ))}
                    {archivedHabits.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
                          Archived
                        </div>
                        {archivedHabits?.map((habit) => (
                          <SelectItem key={habit.id} value={habit.id}>
                            {habit.emoji ? `${habit.emoji} ` : ""}
                            {habit.name ?? "Habit"}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                <Button
                  size="icon"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => setAssignClientHabitDialogOpen(true)}
                  aria-label="Add habit"
                >
                  <Plus className="size-4" />
                </Button>
              </div>
            </div>

            <div className="w-full grid grid-cols-1 lg:grid-cols-[19rem_minmax(0,1fr)] gap-5">
              <Card className="hidden lg:flex sticky top-5 h-[calc(100svh-5.25rem)] max-h-[calc(100svh-5.25rem)] flex-col overflow-hidden">
                <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-3">
                  <h2 className="text-lg font-semibold tracking-tight">
                    Habits
                  </h2>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-8"
                    onClick={() => setAssignClientHabitDialogOpen(true)}
                    aria-label="Add habit"
                  >
                    <Plus className="size-4" />
                  </Button>
                </div>
                <div className="px-4 pb-3">
                  <InputGroup>
                    <InputGroupAddon>
                      <Search className="size-4 text-muted-foreground" />
                    </InputGroupAddon>
                    <InputGroupInput
                      placeholder="Search habits"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </InputGroup>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
                  {filteredHabits.length === 0 &&
                  filteredArchivedHabits.length === 0 ? (
                    <p className="px-1 py-8 text-center text-sm text-muted-foreground">
                      No matching habits
                    </p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {filteredHabits.map((habit) => renderHabit(habit))}

                      {filteredArchivedHabits.length > 0 && (
                        <Collapsible
                          open={archivedExpanded}
                          onOpenChange={setArchivedExpanded}
                        >
                          <CollapsibleTrigger asChild>
                            <Button
                              variant="ghost"
                              className="mt-2 h-9 w-full justify-between px-2"
                            >
                              <span className="font-medium">
                                Archived ({filteredArchivedHabits.length})
                              </span>
                              {archivedExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="flex flex-col gap-2 pt-2">
                            {filteredArchivedHabits.map((habit) =>
                              renderHabit(habit),
                            )}
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </div>
                  )}
                </div>
              </Card>
              <div className="min-w-0">
                {selectedHabit && (
                  <HabitDetailView
                    key={selectedHabit.id}
                    habit={selectedHabit}
                    clientId={clientId}
                    onEditHabitClicked={() => {
                      setEditHabit(selectedHabit);
                      setAssignClientHabitDialogOpen(true);
                    }}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

